/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { buildDetachTargetsPayload } from '../../components/AssetTargetsDialog/helpers/assetProductLinks';
import buildRemovedLinksFromDetachTargets from '../helpers/assets/buildRemovedLinksFromDetachTargets';
import l10n from '../../shared/strings';
import { fetchAssets } from './fetchAssets';
import { fetchListingAssets } from './fetchListingAssets';
import { fetchVariantAssets } from './fetchVariantAssets';
import { getVariants } from './getVariants';
import { resolveListingVariantFetchParamsByListingVariant } from './helpers/resolveListingVariantFetchParams';

const LS_KEY = 'picsio.hideDetachFromTargetsWarning';
const strings = l10n.DETAILS.confirmDetachFromTargets;

export const detachAssetsFromTargets = createAsyncThunk(
  'products/detachAssetsFromTargets',
  async ({
    assetIds,
    masterProductId,
    targets,
    skipConfirm = false,
  }, {
    extra: {
      sdk, Logger, utils, showErrorDialog, showDialogAsync,
    },
    dispatch,
    getState,
  }) => {
    try {
      const apiTargets = buildDetachTargetsPayload({ productId: masterProductId, targets });
      if (!apiTargets.length) return { detached: false };

      if (!skipConfirm && !utils.LocalStorage.get(LS_KEY)) {
        try {
          await showDialogAsync({
            title: strings.title,
            text: strings.text,
            checkbox: {
              label: strings.checkboxLabel,
              value: false,
              onChange: (value) => {
                utils.LocalStorage.set(LS_KEY, value);
              },
            },
            textBtnOk: strings.textBtnOk,
          }).promise;
        } catch (_) {
          return { detached: false };
        }
      }

      await sdk.assets.detachFromTargets(assetIds, apiTargets);

      await dispatch(fetchAssets({ productId: masterProductId }));

      if (targets.listings?.length) {
        const productsState = getState().products;

        await Promise.all(targets.listings.map(async (listing) => {
          await dispatch(fetchListingAssets({
            masterProductId,
            channelId: listing.channelId,
            listingProductId: listing.listingId,
          }));

          if (!listing.listingVariantIds?.length) return;

          await Promise.all(listing.listingVariantIds.map(async (listingVariantId) => {
            const params = resolveListingVariantFetchParamsByListingVariant(
              productsState,
              listing.listingId,
              listingVariantId,
            );
            if (!params) return;

            await dispatch(fetchVariantAssets({
              productId: masterProductId,
              variantId: params.linkVariantId,
              channelId: params.channelId,
              masterVariantId: params.masterVariantId,
            }));
          }));
        }));
      }

      if (targets.masterVariantIds?.length) {
        await dispatch(getVariants({ _id: masterProductId }));
      }

      return {
        detached: true,
        masterProductId,
        assetIds,
        targets,
        removedLinks: buildRemovedLinksFromDetachTargets({ masterProductId, targets }),
      };
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg') || 'Can not remove assets from product';
      showErrorDialog(errorMessage);
      Logger.error(new Error('Error detach assets from targets'), { error }, [
        'DetachAssetsFromTargetsFailed',
        { errorMessage: errorMessage || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(detachAssetsFromTargets.pending, () => { /* noop */ })
    .addCase(detachAssetsFromTargets.fulfilled, () => { /* noop */ })
    .addCase(detachAssetsFromTargets.rejected, (state, { error }) => {
      state.error = error;
    });
};
